import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, API_BASE, refreshAccessToken, setAccessToken, setSessionExpiredHandler } from '@/services/api';
import { queryClient } from '@/services/query';
import { applyReducedMotion, applyTheme } from '@/theme/themes';
import type { Profile } from '@/types';

interface AuthState {
  user: Profile | null;
  loading: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<Profile>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  can: (...perms: string[]) => boolean;
  setTheme: (theme: string) => Promise<void>;
  updatePreferences: (prefs: Partial<Profile['preferences']>) => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const accept = useCallback((p: Profile) => {
    setUser(p);
    applyTheme(p.company.theme);
    applyReducedMotion(!!p.preferences?.reducedMotion);
  }, []);

  // Organisation theme is pushed live from the server, so an admin's change reaches every open browser instantly.
  useEffect(() => {
    if (typeof EventSource === 'undefined') return;
    const es = new EventSource(`${API_BASE}/events/theme`, { withCredentials: true });
    es.addEventListener('theme', (e) => {
      const theme = (JSON.parse((e as MessageEvent).data) as { theme: string }).theme;
      applyTheme(theme);
      setUser((u) => (u && u.company.theme !== theme ? { ...u, company: { ...u.company, theme } } : u));
    });
    return () => es.close();
  }, []);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      setAccessToken(null);
      setUser(null);
      queryClient.clear();
    });
    (async () => {
      try {
        if (await refreshAccessToken()) accept(await api.get<Profile>('/auth/me'));
      } finally {
        setLoading(false);
      }
    })();
  }, [accept]);

  const login = useCallback(
    async (email: string, password: string, remember = false) => {
      const r = await api.post<{ accessToken: string; user: Profile }>('/auth/login', { email, password, remember });
      setAccessToken(r.accessToken);
      accept(r.user);
      return r.user;
    },
    [accept],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setAccessToken(null);
      setUser(null);
      queryClient.clear();
    }
  }, []);

  const reload = useCallback(async () => accept(await api.get<Profile>('/auth/me')), [accept]);

  const permSet = useMemo(() => new Set(user?.permissions ?? []), [user]);
  const can = useCallback((...perms: string[]) => perms.some((p) => permSet.has(p)), [permSet]);

  /** Organisation-wide; only users with settings:manage (Admin / Super Admin) may change it. */
  const setTheme = useCallback(async (theme: string) => {
    const previous = document.documentElement.dataset.theme ?? 'midnight-blue';
    applyTheme(theme);
    setUser((u) => (u ? { ...u, company: { ...u.company, theme } } : u));
    try {
      await api.patch('/settings/company', { theme });
    } catch (e) {
      applyTheme(previous);
      setUser((u) => (u ? { ...u, company: { ...u.company, theme: previous } } : u));
      throw e;
    }
  }, []);

  const updatePreferences = useCallback(async (prefs: Partial<Profile['preferences']>) => {
    const r = await api.patch<{ preferences: Profile['preferences'] }>('/auth/me/preferences', { preferences: prefs });
    setUser((u) => (u ? { ...u, preferences: r.preferences } : u));
    if (prefs.reducedMotion !== undefined) applyReducedMotion(prefs.reducedMotion);
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout, reload, can, setTheme, updatePreferences }), [user, loading, login, logout, reload, can, setTheme, updatePreferences]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
